import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  BottomSheet,
  Button,
  DateField,
  List,
  ListItem,
  Notice,
  SelectField,
  Skeleton,
  StateScreen,
  TextArea,
  TextField,
  TimeField,
  useSnackbar,
} from '@sauda/ui/kit';
import { useChrome } from '../../app/chrome.js';
import { useAuth } from '../../auth/AuthContext.js';
import { ApiError, api } from '../../lib/api.js';
import {
  followUpDue,
  followUpToInstant,
  instantToFollowUp,
  type EnquiryView,
} from '../../lib/enquiry.js';
import { initials } from '../../lib/initials.js';
import { whatsappLink } from '../../lib/share-text.js';
import { formatIst } from '../../lib/time.js';
import type { PropertyView } from '../../lib/types.js';

export function useEnquiries() {
  const auth = useAuth();
  return useQuery({
    queryKey: ['enquiries', auth.agencyId],
    queryFn: () => api<EnquiryView[]>('/enquiries'),
  });
}

/** Add an enquiry by hand: a call or a WhatsApp that never used the booking form. */
export function EnquirySheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [note, setNote] = useState('');
  const [followDate, setFollowDate] = useState('');
  const [followTime, setFollowTime] = useState('09:00');
  const followUp =
    followDate === '' ? '' : `${followDate}T${followTime === '' ? '09:00' : followTime}`;
  const [error, setError] = useState<string | null>(null);
  const properties = useQuery({
    queryKey: ['properties', auth.agencyId],
    queryFn: () => api<PropertyView[]>('/properties'),
    enabled: open,
  });
  const save = useMutation({
    mutationFn: () =>
      api<EnquiryView>('/enquiries', {
        method: 'POST',
        body: {
          name: name.trim(),
          phone: phone.trim(),
          propertyId: propertyId === '' ? null : propertyId,
          note: note.trim() === '' ? null : note.trim(),
          followUpAt: followUpToInstant(followUp),
        },
      }),
    onSuccess: async () => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['enquiries'] }),
        cache.invalidateQueries({ queryKey: ['today'] }),
        cache.invalidateQueries({ queryKey: ['properties'] }),
      ]);
      setName('');
      setPhone('');
      setPropertyId('');
      setNote('');
      setFollowDate('');
      setFollowTime('09:00');
      setError(null);
      onClose();
      snack({ text: t('enquiry.saved') });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError && e.status < 500 && e.message !== ''
          ? e.message
          : t('enquiry.failed'),
      ),
  });
  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      label={t('enquiry.add')}
      title={t('enquiry.add')}
      closeLabel={t('app.close')}
      actions={
        <Button
          size="lg"
          block
          loading={save.isPending}
          disabled={name.trim().length < 2 || phone.trim() === ''}
          onClick={() => save.mutate()}
        >
          {t('enquiry.save')}
        </Button>
      }
    >
      <div className="k-stack">
        {error === null ? null : <Notice tone="error">{error}</Notice>}
        <TextField
          label={t('enquiry.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label={t('enquiry.phone')}
          type="tel"
          inputMode="numeric"
          prefix="+91"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <SelectField
          label={t('enquiry.flat')}
          value={propertyId}
          placeholder={t('enquiry.noFlat')}
          onChange={(e) => setPropertyId(e.target.value)}
          options={(properties.data ?? [])
            .filter((p) => p.status === 'available')
            .map((p) => ({ value: p.id, label: p.title }))}
        />
        <TextArea
          label={t('enquiry.note')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <DateField label={t('enquiry.followUp')} value={followDate} onChange={setFollowDate} />
        {followDate === '' ? null : (
          <TimeField
            label={t('enquiry.followUpTime')}
            value={followTime}
            onChange={setFollowTime}
          />
        )}
      </div>
    </BottomSheet>
  );
}

/** The enquiries the person can see: Call, WhatsApp, and turn one into a visit. */
export function EnquiriesList({ onAdd }: { onAdd: () => void }) {
  const { t } = useTranslation();
  const chrome = useChrome();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const enquiries = useEnquiries();
  const [flatFor, setFlatFor] = useState<EnquiryView | null>(null);
  const refresh = async () => {
    await Promise.all([
      cache.invalidateQueries({ queryKey: ['enquiries'] }),
      cache.invalidateQueries({ queryKey: ['today'] }),
      cache.invalidateQueries({ queryKey: ['visits'] }),
    ]);
  };
  const close = useMutation({
    mutationFn: (id: string) =>
      api(`/enquiries/${id}`, { method: 'PATCH', body: { status: 'closed' } }),
    onSuccess: refresh,
    onError: () => snack({ text: t('enquiry.failed') }),
  });
  const book = useMutation({
    mutationFn: (id: string) =>
      api<{ visitId: string }>(`/enquiries/${id}/visit`, { method: 'POST', body: {} }),
    onSuccess: async (made) => {
      await refresh();
      void navigate(`/visits/${made.visitId}`);
    },
    onError: (e) =>
      snack({
        text:
          e instanceof ApiError && e.status < 500 && e.message !== ''
            ? e.message
            : t('enquiry.failed'),
      }),
  });
  const pick = useMutation({
    mutationFn: (v: { id: string; propertyId: string }) =>
      api(`/enquiries/${v.id}`, { method: 'PATCH', body: { propertyId: v.propertyId } }),
    onSuccess: async () => {
      setFlatFor(null);
      await refresh();
    },
    onError: () => snack({ text: t('enquiry.failed') }),
  });
  const properties = useQuery({
    queryKey: ['properties-pick'],
    queryFn: () => api<PropertyView[]>('/properties'),
    enabled: flatFor !== null,
  });
  if (enquiries.isPending) return <Skeleton height={72} />;
  const open = (enquiries.data ?? []).filter((e) => e.status === 'open');
  const now = Date.now();
  return (
    <>
      {open.length === 0 ? (
        <StateScreen
          kind="empty"
          compact
          title={t('enquiry.empty')}
          action={
            chrome.readOnly ? undefined : (
              <Button variant="tonal" size="lg" onClick={onAdd}>
                {t('enquiry.add')}
              </Button>
            )
          }
        />
      ) : (
        <List label={t('enquiry.title')}>
          {open.map((e) => (
            <ListItem
              key={e.id}
              title={e.name}
              leading={<Avatar initials={initials(e.name)} />}
              supporting={[
                e.propertyTitle ?? t('enquiry.noFlatYet'),
                e.followUpAt === null
                  ? null
                  : `${followUpDue(e.followUpAt, now) ? t('enquiry.due') : t('enquiry.followUpOn')} ${formatIst(e.followUpAt)}`,
                e.note,
              ]
                .filter(Boolean)
                .join(' · ')}
              action={
                <div className="chips-row">
                  <a
                    className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                    href={`tel:${e.phone}`}
                  >
                    {t('visits.call')}
                  </a>
                  <a
                    className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                    href={whatsappLink('', e.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('visits.whatsapp')}
                  </a>
                  {chrome.readOnly ? null : (
                    <Button
                      variant="text"
                      loading={book.isPending && book.variables === e.id}
                      onClick={() => (e.propertyId === null ? setFlatFor(e) : book.mutate(e.id))}
                    >
                      {t('enquiry.bookVisit')}
                    </Button>
                  )}
                  {chrome.readOnly ? null : (
                    <Button variant="text" onClick={() => close.mutate(e.id)}>
                      {t('enquiry.close')}
                    </Button>
                  )}
                </div>
              }
            />
          ))}
        </List>
      )}
      <BottomSheet
        open={flatFor !== null}
        onClose={() => setFlatFor(null)}
        label={t('enquiry.pickFlat')}
        title={t('enquiry.pickFlat')}
        closeLabel={t('app.close')}
      >
        <List label={t('enquiry.pickFlat')}>
          {(properties.data ?? [])
            .filter((p) => p.status === 'available')
            .map((p) => (
              <ListItem
                key={p.id}
                title={p.title}
                onClick={() =>
                  flatFor !== null && pick.mutate({ id: flatFor.id, propertyId: p.id })
                }
              />
            ))}
        </List>
      </BottomSheet>
    </>
  );
}

export { instantToFollowUp };
